/**
 * GENERATED_ASSET — palette roles copied from Helix2000
 * docs/plans/2026-10-20-hush-nook-lanternskip/catalog/render-tokens.json (revision hush-palette-style.1).
 * tidalCascade.test.ts checks these roles against the canonical token catalog.
 * Derived swatches below only mix/shift EXISTING roles; no new hue is introduced as a role.
 */
import type { DetailTier, ResolvedPalette, StyleProfile } from './types';

export const TOKEN_REVISION = 'hush-palette-style.1';

export const PALETTES: Readonly<Record<'coastal_salt' | 'nook_warm', ResolvedPalette>> = {
  coastal_salt: {
    outline: '#2B4145', outlineSoft: '#586D69', paperBase: '#D7BC83', paperLight: '#F0DEB0', paperShadow: '#A88A54',
    warmCore: '#FFE3A0', woodBase: '#826B4D', woodLight: '#A18A61', woodShadow: '#594C3B',
    clothBlue: '#6C9BAE', clothBlueLight: '#AAC8CE', clothBlueShadow: '#426C7D',
    reedBase: '#84956C', reedLight: '#AFB68A', reedShadow: '#5D7459',
    stoneBase: '#8C9C96', stoneLight: '#B5BDA7', stoneShadow: '#627873', moss: '#78966B', salt: '#D1D6BF',
    waterFar: '#527E86', waterLight: '#A9C7C4',
  },
  nook_warm: {
    outline: '#2B4145', outlineSoft: '#586D69', paperBase: '#E5C991', paperLight: '#F5E4BF', paperShadow: '#A88A54',
    warmCore: '#FFE8AB', woodBase: '#927754', woodLight: '#A18A61', woodShadow: '#594C3B',
    clothBlue: '#6C9BAE', clothBlueLight: '#AAC8CE', clothBlueShadow: '#426C7D',
    reedBase: '#8D9B73', reedLight: '#AFB68A', reedShadow: '#5D7459',
    stoneBase: '#949F8B', stoneLight: '#B5BDA7', stoneShadow: '#687C6B', moss: '#78966B', salt: '#D1D6BF',
    waterFar: '#527E86', waterLight: '#A9C7C4',
  },
};

export const STYLE: StyleProfile = {
  outerStroke: 2.1, seamStroke: 1.2, grainStroke: 0.65, grainOpacity: 0.14,
  contactShadowOpacity: 0.18, coordinatePrecision: 3, samplingPadding: 2,
};

export const DETAIL: Readonly<Record<DetailTier, { grainMarks: number; minorRepairs: boolean }>> = {
  readable: { grainMarks: 0, minorRepairs: false },
  standard: { grainMarks: 8, minorRepairs: true },
  close: { grainMarks: 14, minorRepairs: true },
};

type RGB = [number, number, number];
export function parseHex(h: string): RGB {
  const m = /^#([0-9a-f]{6})$/i.exec(h);
  if (!m) throw new Error(`Invalid #RRGGBB color: ${h}`);
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
export function toHex([r, g, b]: RGB): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}
export function mix(a: string, b: string, t: number): string {
  const A = parseHex(a), B = parseHex(b);
  return toHex([A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t]);
}
/** Push a color away from its own luminance gray: >0 more saturated. */
export function saturate(c: string, amt: number): string {
  const [r, g, b] = parseHex(c);
  const l = 0.299 * r + 0.587 * g + 0.114 * b;
  return toHex([l + (r - l) * (1 + amt), l + (g - l) * (1 + amt), l + (b - l) * (1 + amt)]);
}

export interface Swatches {
  outline: string; outlineSoft: string;
  stone: { base: string; light: string; shadow: string; deep: string; seam: string; lit: string };
  slate: { top: string; edge: string; sheen: string; seam: string };
  moss: { base: string; light: string; dark: string };
  salt: string;
  water: { deep: string; mid: string; shallow: string; sheet: string; sheetLight: string; edge: string; foam: string; foamShade: string };
  brass: { base: string; light: string; dark: string; rim: string; tarnish: string };
}

/** Material swatches: stone is grey-green, slate cooler/darker, water saturated teal, brass warm. */
export function deriveSwatches(p: ResolvedPalette): Swatches {
  const slateTop = mix(p.stoneBase, p.clothBlueShadow, 0.34);
  const deepWater = saturate(mix(p.waterFar, p.clothBlueShadow, 0.2), 0.45);
  const midWater = saturate(mix(p.waterFar, p.waterLight, 0.38), 0.7);
  const shallow = saturate(mix(p.waterLight, p.clothBlueLight, 0.15), 0.55);
  const brass = mix(p.paperBase, p.paperShadow, 0.35);
  return {
    outline: p.outline, outlineSoft: p.outlineSoft,
    stone: {
      base: p.stoneBase, light: p.stoneLight, shadow: p.stoneShadow,
      deep: mix(p.stoneShadow, p.outline, 0.45), seam: mix(p.stoneShadow, p.outline, 0.55),
      lit: mix(p.stoneBase, p.stoneLight, 0.55),
    },
    slate: {
      top: slateTop, edge: mix(p.stoneShadow, p.outline, 0.35),
      sheen: mix(p.stoneLight, p.clothBlueLight, 0.35), seam: mix(slateTop, p.outline, 0.5),
    },
    moss: { base: p.moss, light: p.reedLight, dark: p.reedShadow },
    salt: p.salt,
    water: {
      deep: deepWater, mid: midWater, shallow,
      sheet: saturate(mix(p.waterLight, p.waterFar, 0.28), 0.6),
      sheetLight: mix(p.waterLight, '#FFFFFF', 0.35),
      edge: mix(deepWater, p.outline, 0.5),
      foam: mix(p.salt, '#FFFFFF', 0.45), foamShade: mix(p.waterLight, p.clothBlueLight, 0.5),
    },
    brass: {
      base: brass, light: p.warmCore, dark: mix(p.paperShadow, p.woodBase, 0.55),
      rim: mix(p.woodShadow, p.paperShadow, 0.3), tarnish: mix(p.reedBase, p.clothBlue, 0.4),
    },
  };
}
